import parsePhoneNumber, { type E164Number } from 'libphonenumber-js';
import { Controller, useForm } from 'react-hook-form';

import { Button, Stack, TextField, Typography } from '@mui/material';
import { PhoneInput } from '@sage/ui-components';
import { createFileRoute } from '@tanstack/react-router';

import PageFooter from 'src/components/PageFooter';
import PageHeader from 'src/components/PageHeader';
import StyledLinearProgressBar from 'src/components/StyledLinearProgressBar';
import StyledRadioGroup from 'src/components/StyledRadioGroup';
import { PreferredCountryCodes, SupportedCountryCodes } from 'src/constants/SupportedCountryCodes';
import { TeamRoles } from 'src/constants/TeamRoles';
import { useOnboardingWorkflow } from 'src/contexts/OnboardingWorkflowContext';
import { useSnackbar } from 'src/contexts/SnackbarContext';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { useInviteTeamMemberMutation, type TeamMemberRole } from 'src/store/sageHomeApi';
import {
    getPhoneNumberValidationRules,
    limitPhoneNumberLength,
} from 'src/utils/phoneNumberInputUtils';

const roleOptions: { value: TeamMemberRole; label: string }[] = [
    {
        value: TeamRoles.LicensedCaregiver,
        label: 'Professional caregiver',
    },
    {
        value: TeamRoles.CommunityCaregiver,
        label: 'Family member or friend',
    },
];

export const Route = createFileRoute('/(app)/onboarding/invite-caregiver')({
    component: InviteCaregiverComponent,
    validateSearch: (search: Record<string, unknown>) => {
        return {
            role: (search.role as string) || TeamRoles.CommunityCaregiver,
        };
    },
});

function InviteCaregiverComponent() {
    const { showError } = useSnackbar();

    const { currentTeam } = useTeamMemberData();

    const { progress, nextStep, setInvitedTeamMember, isProcessing } = useOnboardingWorkflow();

    const [inviteTeamMember, { isLoading: isInviting }] = useInviteTeamMemberMutation();
    const { handleSubmit, control, formState } = useForm<{
        teamMemberRole: TeamMemberRole;
        firstName: string;
        lastName: string;
        phoneNumber: string;
        email: string;
    }>({
        mode: 'onChange',
        defaultValues: {
            teamMemberRole: TeamRoles.LicensedCaregiver,
            firstName: '',
            lastName: '',
            phoneNumber: '',
            email: '',
        },
    });

    const onSubmit = async (data: {
        teamMemberRole: TeamMemberRole;
        firstName: string;
        lastName: string;
        phoneNumber: string;
        email: string;
    }) => {
        if (!currentTeam?.teamRid) {
            console.error('No team selected');
            return;
        }

        try {
            const invitedTeamMember = await inviteTeamMember({
                body: {
                    teamRid: currentTeam.teamRid,
                    teamRole: data.teamMemberRole,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    email: data.email,
                    phoneNumber: parsePhoneNumber(data.phoneNumber)?.number as E164Number,
                    isTeamAdmin: false,
                },
            }).unwrap();

            setInvitedTeamMember(invitedTeamMember);

            nextStep();
        } catch (error) {
            console.error('Failed to invite team member:', error);
            showError('An error occurred, please try again.');
        }
    };

    const handlePhoneNumberChange = (value: string, onChange: (value: string) => void) => {
        if (value.startsWith('+1 1 ')) {
            onChange(limitPhoneNumberLength(value.replace('+1 1 ', '+1 ')));
        } else if (!value.startsWith('+1')) {
            onChange(limitPhoneNumberLength(`+1 ${value}`));
        } else {
            onChange(limitPhoneNumberLength(value));
        }
    };

    return (
        <>
            <PageHeader hideBackButton hideActionButton>
                <StyledLinearProgressBar value={progress.percentage} />
            </PageHeader>

            <Typography variant="h1" sx={{ fontWeight: 'bold' }}>
                Invite a Team Member
            </Typography>

            <Stack sx={{ gap: 4, py: 2 }}>
                <Controller
                    name="teamMemberRole"
                    control={control}
                    rules={{ required: 'Please select a role' }}
                    render={({ field: { onChange, value } }) => (
                        <StyledRadioGroup options={roleOptions} value={value} onChange={onChange} />
                    )}
                />

                <Controller
                    name="phoneNumber"
                    control={control}
                    rules={getPhoneNumberValidationRules({ isRequired: true })}
                    render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                        <PhoneInput
                            onlyCountries={SupportedCountryCodes}
                            preferredCountries={PreferredCountryCodes}
                            defaultCountry={PreferredCountryCodes[0]}
                            value={value ?? ''}
                            inputRef={ref}
                            onChange={(value) => handlePhoneNumberChange(value, onChange)}
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                />

                <Controller
                    name="firstName"
                    control={control}
                    render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                        <TextField
                            inputRef={ref}
                            fullWidth
                            label="First Name"
                            variant="outlined"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                    rules={{ required: 'First name is required' }}
                />

                <Controller
                    name="lastName"
                    control={control}
                    render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                        <TextField
                            inputRef={ref}
                            fullWidth
                            label="Last Name"
                            variant="outlined"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                    rules={{ required: 'Last name is required' }}
                />

                <Controller
                    name="email"
                    control={control}
                    render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
                        <TextField
                            inputRef={ref}
                            fullWidth
                            label="Email"
                            variant="outlined"
                            value={value}
                            onChange={onChange}
                            error={!!error}
                            helperText={error?.message}
                        />
                    )}
                    rules={{
                        required: 'Email is required',
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address',
                        },
                    }}
                />
                <Stack sx={{ gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Please confirm the phone number is correct. They will login using that
                        mobile number.
                    </Typography>
                </Stack>
            </Stack>

            <PageFooter>
                <Button
                    variant="contained"
                    fullWidth
                    color="primary"
                    onClick={handleSubmit(onSubmit)}
                    disabled={!formState.isValid}
                    loading={isInviting || isProcessing}
                >
                    Continue
                </Button>
            </PageFooter>
        </>
    );
}
